import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { AppearanceColorKey, IAppearanceColor, IFont } from '@web/dtos';
import { ICorporateDesignForm } from '../../entities/coporate-design-entity';
import { CmsTextProviderService } from '../../services/cms-text-provider.service';
import { AppearanceFontUploadComponent } from '../appearance-font-upload/appearance-font-upload.component';
import { SectionComponent } from '../layout/section/section.component';
import { SectionHeaderComponent } from '../layout/section/section-header/section-header.component';
import { ColorPickerComponent } from '../data-input/color-picker/color-picker.component';
import { ColorPickerListComponent } from '../data-input/color-picker-list/color-picker-list';

@Component({
  selector: 'aw-appearance-highlighted-actions',
  imports: [
    AppearanceFontUploadComponent,
    ColorPickerComponent,
    NgClass,
    NgTemplateOutlet,
    SectionComponent,
    SectionHeaderComponent,
    ColorPickerListComponent
  ],
  templateUrl: './appearance-highlighted-actions.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppearanceHighlightedActionsComponent {
  public readonly forceCollapse: InputSignal<boolean> = input(false);
  public readonly designForm: InputSignal<FormGroup<ICorporateDesignForm>> =
    input.required<FormGroup<ICorporateDesignForm>>();
  public readonly fontDraftChange: OutputEmitterRef<boolean> =
    output<boolean>();
  private readonly fontUpload: Signal<
    AppearanceFontUploadComponent | undefined
  > = viewChild(AppearanceFontUploadComponent);
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );

  protected readonly colors: IAppearanceColor[] = [
    {
      key: 'fill',
      label: this.cmsTextProvider.appearanceText.ctaFillColour,
      description: this.cmsTextProvider.appearanceText.ctaFillColourHint,
    },
    {
      key: 'contrast',
      label: this.cmsTextProvider.appearanceText.ctaContrast,
    },
    {
      key: 'border',
      label: this.cmsTextProvider.appearanceText.ctaBorder,
      description: this.cmsTextProvider.appearanceText.ctaBorderHint,
    },
    {
      key: 'outline',
      label: this.cmsTextProvider.appearanceText.ctaOutline,
      description: this.cmsTextProvider.appearanceText.ctaOutlineHint,
    },
  ];

  protected fillColor(isDark: boolean): string {
    return this.colorControl('fill', isDark).value;
  }

  protected contrastColor(isDark: boolean): string {
    return this.colorControl('contrast', isDark).value;
  }

  protected borderColor(isDark: boolean): string {
    return this.colorControl('border', isDark).value;
  }

  protected outlineColor(isDark: boolean): string {
    return this.colorControl('outline', isDark).value;
  }

  protected swatchColor(key: AppearanceColorKey, isDark: boolean): string {
    switch (key) {
      case 'fill':
        return this.fillColor(isDark);
      case 'contrast':
        return this.contrastColor(isDark);
      case 'border':
        return this.borderColor(isDark);
      case 'outline':
        return this.outlineColor(isDark);
    }
  }

  protected colorControl(
    key: AppearanceColorKey,
    isDark: boolean
  ): FormControl<string> {
    switch (key) {
      case 'fill':
        return isDark
          ? this.designForm().controls.secondaryDarkColor
          : this.designForm().controls.secondaryColor;
      case 'contrast':
        return isDark
          ? this.designForm().controls.secondaryDarkFontColor
          : this.designForm().controls.secondaryFontColor;
      case 'border':
        return isDark
          ? this.designForm().controls.secondaryDarkBorderColor
          : this.designForm().controls.secondaryBorderColor;
      case 'outline':
        return isDark
          ? this.designForm().controls.secondaryDarkAlternativeColor
          : this.designForm().controls.secondaryAlternativeColor;
    }
  }

  protected selectColor(
    key: AppearanceColorKey,
    isDark: boolean,
    color: string
  ): void {
    this.colorControl(key, isDark).setValue(`#${color.replace(/^#/, '')}`);
  }

  public getPendingFontFile(): File | null {
    return this.fontUpload()?.getPendingFile() ?? null;
  }

  public isDefaultFontSelected(): boolean {
    return this.fontUpload()?.isDefaultSelected() ?? false;
  }

  public resetFontDraft(): void {
    this.fontUpload()?.resetDraft();
  }

  public commitSavedFont(font: IFont | null): void {
    this.fontUpload()?.commitSavedFont(font);
  }
}
